import { PokerRunEvent } from "@models/PokerRunEvent";
import { EventParticipant } from "@models/EventParticipant";
import { ParticipantStop } from "@models/ParticipantStop";
import { Stop } from "@models/Stop";
import useDate from "./useDate";
import Constants from "@/models/Constants";

export default function useEvent(): {
  hasRegistrationDatePast: (pokerRunEvent: PokerRunEvent) => boolean;
  getHandsSoldDisplay: (pokerRunEvent: PokerRunEvent) => string;
  getCardReleaseDisplay: (pokerRunEvent: PokerRunEvent) => string;
  isPastEvent: (pokerRunEvent: PokerRunEvent) => boolean;
  isEventActive: (pokerRunEvent: PokerRunEvent) => boolean;
  getStopName: (pokerRunEvent: PokerRunEvent, stopId: number) => string | undefined;
  getRegistrationFee: (pokerRunEvent: PokerRunEvent) => number | null;
  isFreeEvent: (pokerRunEvent: PokerRunEvent) => boolean;

  // Utility functions for event data
  isUserInEvent: (eventDate: string, hasParticipants: boolean) => boolean;
  canUserCheckin: (eventDate: string, eventStatus: string) => boolean;
  getFirstMissedStop: (event: PokerRunEvent, participant: EventParticipant) => Stop | null;
  getNextStop: (event: PokerRunEvent, lastCheckedInStop: ParticipantStop | null) => Stop | null;
  getLastCheckedInStop: (participant: EventParticipant) => ParticipantStop | null;
  hasNextStopAvailable: (participant: EventParticipant) => boolean;
  getReleaseStyleText: (style: string, cardReleaseType?: string) => string;
  getReleaseStyleDescription: (style: string, cardReleaseType?: string) => string;
} {
  const { today, getDate } = useDate();

  const hasRegistrationDatePast = (pokerRunEvent: PokerRunEvent) => {
    return pokerRunEvent.eventSettings.registrationStartDate <= today.toISOString();
  };

  const getHandsSoldDisplay = (pokerRunEvent: PokerRunEvent) => {
    if (pokerRunEvent.eventSettings.registrationStartDate > today.toISOString()) {
      return `Registration opens ${getDate(
        pokerRunEvent.eventSettings.registrationStartDate as string
      )?.toLocaleDateString()}`;
    }

    if (
      pokerRunEvent.eventSettings.registrationCap &&
      pokerRunEvent.eventSettings.registrationCap > 0
    ) {
      const registrationCount = pokerRunEvent._internal?.registrationCount ?? 0;
      const registrationStatus =
        registrationCount >= pokerRunEvent.eventSettings.registrationCap
          ? "full"
          : pokerRunEvent.eventSettings.registrationStatus;
      return `${pokerRunEvent._internal?.registrationCount ?? 0}/${
        pokerRunEvent.eventSettings.registrationCap
      } hands (${registrationStatus})`;
    }
    return `${pokerRunEvent._internal?.registrationCount ?? 0} hands (${
      pokerRunEvent.eventSettings.registrationStatus
    })`;
  };

  const getCardReleaseDisplay = (pokerRunEvent: PokerRunEvent) => {
    const careReleaseMap = {
      manual: "QR code scan",
      schedule: "Scheduled time",
      geo: "GPS",
    };
    return careReleaseMap[pokerRunEvent.eventSettings.cardReleaseType];
  };

  const isPastEvent = (pokerRunEvent: PokerRunEvent) => {
    if (!pokerRunEvent.stops || pokerRunEvent.stops.length === 0) {
      const eventDate = getDate(pokerRunEvent.eventDate as string);
      return eventDate !== null && eventDate < today;
    }

    const endDate = pokerRunEvent.stops[pokerRunEvent.stops.length - 1]?.expectedDepartureTime || pokerRunEvent.eventDate;
    const lastDeparture = getDate(
      endDate as string
    ) as Date;
    lastDeparture?.setHours(23, 59, 59, 0);
    return lastDeparture < today;
  };

  const getStopName = (pokerRunEvent: PokerRunEvent, stopId: number) => {
    return pokerRunEvent.stops.find((stop) => stop.order === stopId)?.name;
  };

  const getReleaseStyleText = (style: string, cardReleaseType?: string) => {
    if (cardReleaseType === 'schedule') {
      return Constants.CARD_RELEASE_TYPE_SCHEDULE_TEXT;
    }

    switch (style) {
      case "relaxed":
        return Constants.CARD_RELEASE_STYLE_RELAXED_TEXT;
      case "standard":
        return Constants.CARD_RELEASE_STYLE_STANDARD_TEXT;
      case "scavenger":
        return Constants.CARD_RELEASE_STYLE_SCAVENGER_TEXT;
      case "strict":
        return Constants.CARD_RELEASE_STYLE_STRICT_TEXT;
      default:
        return Constants.CARD_RELEASE_STYLE_RELAXED_TEXT;
    }
  };

  const getReleaseStyleDescription = (style: string, cardReleaseType?: string) => {
    if (cardReleaseType === 'schedule') {
      return Constants.CARD_RELEASE_TYPE_SCHEDULE_DESC;
    }

    switch (style) {
      case "relaxed":
        return Constants.CARD_RELEASE_STYLE_RELAXED_DESC;
      case "standard":
        return Constants.CARD_RELEASE_STYLE_STANDARD_DESC;
      case "scavenger":
        return Constants.CARD_RELEASE_STYLE_SCAVENGER_DESC;
      case "strict":
        return Constants.CARD_RELEASE_STYLE_STRICT_DESC;
      default:
        return Constants.CARD_RELEASE_STYLE_RELAXED_DESC;
    }
  };

  const getRegistrationFee = (pokerRunEvent: PokerRunEvent) => {
    // find min fee
    if (!pokerRunEvent.lineItems) return null;
    const item = pokerRunEvent.lineItems
      .filter((i) => i.itemType === Constants.FEE_TYPE)
      .reduce((prev, curr) => (prev.amount < curr.amount ? prev : curr));
    if (item) {
      return item.amount;
    }
    return null;
  };

  const isFreeEvent = (pokerRunEvent: PokerRunEvent): boolean => {
    return (
      pokerRunEvent.lineItems
        .filter((item) => item.itemType === Constants.FEE_TYPE)
        .every((item) => item.amount === 0) && (pokerRunEvent._internal?.serviceFee ?? 0) === 0
    );
  };

  const isEventActive = (pokerRunEvent: PokerRunEvent): boolean => {
    if(!pokerRunEvent?.eventDate) return false;
    const eventDate = getDate(pokerRunEvent.eventDate as string);
    // If event has no stops, check only the event date and status
    if (!pokerRunEvent.stops || pokerRunEvent.stops.length === 0) {
      return eventDate !== null && eventDate <= today && pokerRunEvent.status !== "completed" as any;
    }

    // For events with stops, check if the event has started and not completed
    if (eventDate === null || eventDate > today || pokerRunEvent.status === "completed" as any) {
      return false;
    }

    // For multi-day events, check the latest departure time
    const lastStop = pokerRunEvent.stops[pokerRunEvent.stops.length - 1];
    if (lastStop && lastStop.expectedDepartureTime) {
      const lastDeparture = getDate(lastStop.expectedDepartureTime as string) as Date;
      if (lastDeparture) {
        lastDeparture.setHours(23, 59, 59, 0);
        return eventDate <= today && lastDeparture >= today;
      }
    }

    // Default to checking event date if no stop information is available
    return eventDate === today && pokerRunEvent.status !== "completed" as any;
  };

  // Get formatISODate from useDate
  const { formatISODate } = useDate();

  // Check if user is in an event today
  const isUserInEvent = (eventDate: string, hasParticipants: boolean): boolean => {
    if (!eventDate) return false;

    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);

    return (
      eventDate !== null &&
      eventDate !== undefined &&
      eventDate.substring(0, 10) === formatISODate(todayDate) &&
      hasParticipants
    );
  };

  // Check if user can check in to an event
  const canUserCheckin = (eventDate: string, eventStatus: string): boolean => {
    if (!eventDate) return false;

    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);

    return (
      eventDate !== null &&
      eventDate !== undefined &&
      eventStatus === "active" &&
      eventDate.substring(0, 10) === formatISODate(todayDate)
    );
  };

  // Get the last checked-in stop for a participant
  const getLastCheckedInStop = (participant: EventParticipant): ParticipantStop | null => {
    if (!participant || !participant.stops || participant.stops.length === 0) return null;

    const checkedInStops = participant.stops.filter(x => x.checkedIn);
    return checkedInStops.length > 0 ? checkedInStops[checkedInStops.length - 1] : null;
  };

  // Check if there is a next stop available for a participant
  const hasNextStopAvailable = (participant: EventParticipant): boolean => {
    if (!participant || !participant.stops) return false;
    return participant.stops.some(stop => !stop.checkedIn);
  };

  // Get the next stop for an event based on the last checked-in stop
  const getNextStop = (event: PokerRunEvent, lastCheckedInStop: ParticipantStop | null): Stop | null => {
    if (!event || !event.stops || event.stops.length === 0) return null;

    if (lastCheckedInStop !== null) {
      return event.stops.find(
        x => x.order === (lastCheckedInStop.order ?? 0) + 1
      ) as Stop || null;
    }
    return event.stops[0];
  };

  // Get the first missed stop for a participant
  const getFirstMissedStop = (event: PokerRunEvent, participant: EventParticipant): Stop | null => {
    if (!event || !participant || !participant.stops || participant.stops.length === 0) return null;

    // No missed stops in scavenger hunt mode
    if (event.eventSettings.cardReleaseStyle === Constants.CARD_RELEASE_STYLE_SCAVENGER) {
      return null;
    }

    const maxCheckedInStop = participant.stops
      .filter(x => x.checkedIn)
      .reduce((prev, current) => (prev.order > current.order ? prev : current), { order: 0 });

    const minNotCheckedInStop = participant.stops
      .filter(x => !x.checkedIn)
      .reduce((prev, current) => (prev.order < current.order ? prev : current), { order: 1000 });

    if (maxCheckedInStop === undefined || minNotCheckedInStop === undefined) return null;
    if (maxCheckedInStop.order === 0 || minNotCheckedInStop.order === 1000) return null;

    const missedStop = event.stops.find(
      x => x.order === minNotCheckedInStop.order
    );

    return maxCheckedInStop.order > minNotCheckedInStop.order ? missedStop ?? null : null;
  };

  return {
    hasRegistrationDatePast,
    getHandsSoldDisplay,
    getCardReleaseDisplay,
    isPastEvent,
    isEventActive,
    getStopName,
    getReleaseStyleText,
    getReleaseStyleDescription,
    getRegistrationFee,
    isFreeEvent,
    isUserInEvent,
    canUserCheckin,
    getFirstMissedStop,
    getNextStop,
    getLastCheckedInStop,
    hasNextStopAvailable,
  };
}
